// Local-first draft store for unauthenticated offering creation.
// Lives entirely in localStorage so anon users can edit without any
// network round-trip. The first server write happens at publish time
// via publishDraft (src/lib/actions/publish-draft.ts).

import type { EditorOffering } from "@/components/editor/offering-editor";

export type DraftEnvelope = {
  version: 1;
  localId: string;
  updatedAt: number;
  data: EditorOffering;
};

const STORAGE_PREFIX = "offering-draft:";
const INDEX_KEY = "offering-drafts:index";

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/**
 * Returns true only if a small write-then-delete probe succeeds. Catches the
 * Safari Private Mode and "storage disabled / blocked by extension" cases
 * where `window.localStorage` exists but throws on any setItem.
 */
export function canUseLocalStorage(): boolean {
  if (!isBrowser()) return false;
  try {
    const probe = "__offerings_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function key(localId: string) {
  return `${STORAGE_PREFIX}${localId}`;
}

function readIndex(): string[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeIndex(ids: string[]) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(INDEX_KEY, JSON.stringify(ids));
  } catch {
    /* quota / private mode — drop silently */
  }
}

export function loadDraft(localId: string): DraftEnvelope | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(key(localId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope;
    if (parsed?.version !== 1 || parsed.localId !== localId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveDraft(localId: string, data: EditorOffering): void {
  if (!isBrowser()) return;
  const envelope: DraftEnvelope = {
    version: 1,
    localId,
    updatedAt: Date.now(),
    data,
  };
  try {
    window.localStorage.setItem(key(localId), JSON.stringify(envelope));
  } catch {
    return;
  }
  const idx = readIndex();
  if (!idx.includes(localId)) {
    idx.push(localId);
    writeIndex(idx);
  }
}

export function deleteDraft(localId: string): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key(localId));
  } catch {
    /* ignore */
  }
  const idx = readIndex().filter((id) => id !== localId);
  writeIndex(idx);
}

export function listDrafts(): DraftEnvelope[] {
  if (!isBrowser()) return [];
  const idx = readIndex();
  const out: DraftEnvelope[] = [];
  for (const id of idx) {
    const env = loadDraft(id);
    if (env) out.push(env);
  }
  return out;
}

export function newLocalId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `local_${crypto.randomUUID()}`;
  }
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
